import type { JsonSchemaType } from '@gmn-portal/backend';
import { Helicopter, Link2, Stethoscope } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useObjectCategories, useObjects } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import type { Object as PortalObject } from '@/features/shared/objects/types';
import { EntityReferenceValue } from '@/shared/components/layout';
import { ContactEmailLink } from '@/shared/components/metadata/contact-email-link';
import { ContactPhoneLink } from '@/shared/components/metadata/contact-phone-link';
import { Badge } from '@/shared/components/ui';
import { cn, getObjectDetailHref } from '@/shared/utils';

type RelationDisplayVariant = 'ship-linked-windpark-helicopters' | 'windpark-linked-ship-medics';
const DEFAULT_MEDIC_CONTACT_FIELD_KEYS = ['medicHospitalContact'];

type RelationDisplayFieldProps = {
  fieldKey: string;
  metadataSchema: JsonSchemaType;
  label?: string;
  object?: PortalObject | null;
  canReadObjects?: boolean;
  domain?: 'gmn' | 'customer' | null;
  className?: string;
  preview?: boolean;
};

const normalizeRelationshipId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value && typeof value.id === 'string') {
    return value.id;
  }
  return null;
};

const hasContactContent = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const contact = value as Record<string, unknown>;
  return ['name', 'email', 'phone1', 'phone2'].some((key) => {
    const entry = contact[key];
    return typeof entry === 'string' && entry.trim().length > 0;
  });
};

const normalizeCategoryId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value && typeof value.id === 'string') {
    return value.id;
  }
  return null;
};

const isObjectActiveToday = (object: {
  activePeriods?: Array<{ startDate?: string | null; endDate?: string | null }> | null;
}) => {
  const today = DateTime.now().toFormat('yyyy-MM-dd');
  return (object.activePeriods || []).some((period) => {
    const startDate = period.startDate;
    const endDate = period.endDate;
    if (!startDate || !endDate) return false;
    return startDate <= today && endDate >= today;
  });
};

export function RelationDisplayField({
  fieldKey,
  metadataSchema,
  label,
  object,
  canReadObjects = false,
  domain = null,
  className,
  preview = false,
}: RelationDisplayFieldProps) {
  const { t, locale } = useIntl();
  const field = metadataSchema.properties?.[fieldKey];
  const variant = field?.relationDisplayVariant as RelationDisplayVariant | undefined;
  const configuredContactFieldKeys = Array.isArray(field?.sourceContactFieldKeys)
    ? field.sourceContactFieldKeys.filter((entry): entry is string => typeof entry === 'string')
    : [];
  const medicContactFieldKeys =
    configuredContactFieldKeys.length > 0
      ? configuredContactFieldKeys
      : DEFAULT_MEDIC_CONTACT_FIELD_KEYS;

  const linkedWindparkId = normalizeRelationshipId(object?.linkedWindpark);
  const currentObjectId = typeof object?.id === 'string' ? object.id : null;

  const { data: linkedWindparkData } = useObjects({
    where: linkedWindparkId
      ? {
          id: {
            equals: linkedWindparkId,
          },
        }
      : undefined,
    limit: 1,
    disabled: !linkedWindparkId || preview || variant !== 'ship-linked-windpark-helicopters',
  });

  const windparkLinkedHelicopterIds = useMemo(() => {
    const windpark = linkedWindparkData?.docs?.[0];
    if (!windpark || !Array.isArray(windpark.linkedHelicopters)) return [];
    return windpark.linkedHelicopters
      .map((entry) => normalizeRelationshipId(entry))
      .filter((entry): entry is string => Boolean(entry));
  }, [linkedWindparkData?.docs]);

  const { data: linkedHelicopters } = useObjects({
    where:
      windparkLinkedHelicopterIds.length > 0
        ? {
            id: {
              in: windparkLinkedHelicopterIds,
            },
          }
        : undefined,
    sort: 'name',
    limit: 100,
    disabled:
      windparkLinkedHelicopterIds.length === 0 ||
      preview ||
      variant !== 'ship-linked-windpark-helicopters',
  });

  const { data: linkedShips } = useObjects({
    where: currentObjectId
      ? {
          linkedWindpark: {
            equals: currentObjectId,
          },
        }
      : undefined,
    sort: 'name',
    limit: 100,
    disabled: !currentObjectId || preview || variant !== 'windpark-linked-ship-medics',
  });

  const activeLinkedShips = useMemo(
    () => (linkedShips?.docs || []).filter((doc) => isObjectActiveToday(doc)),
    [linkedShips?.docs]
  );

  const linkedShipCategoryIds = useMemo(
    () =>
      Array.from(
        new Set(
          activeLinkedShips
            .map((doc) => normalizeCategoryId(doc.category))
            .filter((entry): entry is string => Boolean(entry))
        )
      ),
    [activeLinkedShips]
  );

  const { data: linkedShipCategories } = useObjectCategories({
    where:
      linkedShipCategoryIds.length > 0
        ? {
            id: {
              in: linkedShipCategoryIds,
            },
          }
        : undefined,
    limit: linkedShipCategoryIds.length || 1,
    disabled:
      linkedShipCategoryIds.length === 0 || preview || variant !== 'windpark-linked-ship-medics',
  });

  const contactFieldLabels = useMemo(() => {
    const result = new Map<string, string>();

    for (const category of linkedShipCategories?.docs || []) {
      const properties = (category.metadataSchema as JsonSchemaType | undefined)?.properties || {};

      for (const [contactFieldKey, value] of Object.entries(properties)) {
        if (value.type !== 'contact' || result.has(contactFieldKey)) continue;
        result.set(
          contactFieldKey,
          locale === 'en-US' && value.description_en?.trim()
            ? value.description_en
            : value.description || contactFieldKey
        );
      }
    }

    return result;
  }, [linkedShipCategories?.docs, locale]);

  const helicopterItems = useMemo(
    () =>
      (linkedHelicopters?.docs || []).map((doc) => ({
        id: String(doc.id),
        name: doc.name || String(doc.id),
        href: getObjectDetailHref({
          objectId: String(doc.id),
          domain,
          canReadObjects,
        }),
      })),
    [canReadObjects, domain, linkedHelicopters?.docs]
  );

  const medicItems = useMemo(
    () =>
      activeLinkedShips
        .map((doc) => {
          const metadataValues = doc.categoryMetadataValues as Record<string, unknown> | undefined;
          const contacts = medicContactFieldKeys
            .map((contactFieldKey) => ({
              fieldKey: contactFieldKey,
              label: contactFieldLabels.get(contactFieldKey) || contactFieldKey,
              contact: metadataValues?.[contactFieldKey],
            }))
            .filter((entry) => hasContactContent(entry.contact));

          if (contacts.length === 0) return null;

          return {
            id: String(doc.id),
            shipName: doc.name || String(doc.id),
            href: getObjectDetailHref({
              objectId: String(doc.id),
              domain,
              canReadObjects,
            }),
            contacts,
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
    [activeLinkedShips, canReadObjects, contactFieldLabels, domain, medicContactFieldKeys]
  );

  if (!variant) {
    return null;
  }

  if (preview || !object) {
    return (
      <div
        className={cn('rounded-xl border border-dashed border-border bg-muted/20 p-4', className)}
        data-testid={`relation-display-preview-${fieldKey}`}
      >
        <div className="text-xs font-medium text-muted-foreground">
          {label || t('metadata.relationDisplay.preview')}
        </div>
      </div>
    );
  }

  if (variant === 'ship-linked-windpark-helicopters') {
    return (
      <div
        className={cn('rounded-xl border border-border bg-card p-4 space-y-3', className)}
        data-testid={`relation-display-field-${fieldKey}`}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <Helicopter className="size-4 text-muted-foreground" />
          <span>{label || t('objects.linkedWindparkHelicopters')}</span>
        </div>
        {linkedWindparkId ? (
          helicopterItems.length > 0 ? (
            <div className="space-y-2">
              {helicopterItems.map((item) => (
                <div key={item.id} className="text-sm">
                  <EntityReferenceValue label={item.name} href={item.href} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {t('objects.noLinkedWindparkHelicopters')}
            </div>
          )
        ) : (
          <div className="text-sm text-muted-foreground">
            {t('objects.noLinkedWindparkAssigned')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('rounded-xl border border-border bg-card p-4 space-y-3', className)}
      data-testid={`relation-display-field-${fieldKey}`}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Stethoscope className="size-4 text-muted-foreground" />
        <span>{label || t('objects.linkedShipMedics')}</span>
      </div>
      {medicItems.length > 0 ? (
        <div className="space-y-3">
          {medicItems.map((item) => {
            return (
              <div key={item.id} className="rounded-lg border border-border/70 p-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium">
                    <EntityReferenceValue label={item.shipName} href={item.href} />
                  </div>
                  <Badge variant="outline-solid" className="shrink-0">
                    {t('objects.sourceShip')}
                  </Badge>
                </div>
                <div className="space-y-3">
                  {item.contacts.map(
                    ({ fieldKey: contactFieldKey, label: contactLabel, contact }) => {
                      const contactValue = contact as Record<string, unknown>;
                      return (
                        <div
                          key={`${item.id}-${contactFieldKey}`}
                          className="rounded-lg border border-border bg-muted/20 p-3 space-y-3"
                        >
                          {medicContactFieldKeys.length > 1 ? (
                            <div className="text-xs font-medium text-muted-foreground">
                              {contactLabel}
                            </div>
                          ) : null}
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div className="text-sm">
                              <div className="text-xs text-muted-foreground">
                                {t('metadata.contact.name')}
                              </div>
                              <div className="font-medium">
                                {typeof contactValue.name === 'string' && contactValue.name.trim()
                                  ? contactValue.name
                                  : '-'}
                              </div>
                            </div>
                            <div className="text-sm">
                              <div className="text-xs text-muted-foreground">
                                {t('metadata.contact.email')}
                              </div>
                              <div className="font-medium">
                                <ContactEmailLink
                                  value={
                                    typeof contactValue.email === 'string'
                                      ? contactValue.email
                                      : undefined
                                  }
                                />
                              </div>
                            </div>
                            <div className="text-sm">
                              <div className="text-xs text-muted-foreground">
                                {t('metadata.contact.phone1')}
                              </div>
                              <div className="font-medium">
                                <ContactPhoneLink
                                  value={
                                    typeof contactValue.phone1 === 'string'
                                      ? contactValue.phone1
                                      : undefined
                                  }
                                />
                              </div>
                            </div>
                            <div className="text-sm">
                              <div className="text-xs text-muted-foreground">
                                {t('metadata.contact.phone2')}
                              </div>
                              <div className="font-medium">
                                <ContactPhoneLink
                                  value={
                                    typeof contactValue.phone2 === 'string'
                                      ? contactValue.phone2
                                      : undefined
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">{t('objects.noLinkedShipMedics')}</div>
      )}
      <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
        <Link2 className="size-3.5" />
        <span>{t('objects.linkedShipMedicsHint')}</span>
      </div>
    </div>
  );
}
